import React from 'react';
import { useIsLabViewer } from '../../hooks/useIsLabViewer';
import Button from 'react-bootstrap/Button';
import Table from 'react-bootstrap/Table';
import { PencilFill } from 'react-bootstrap-icons';
import { deleteBudgetLine } from '../../clients/budget_line_client';
import DeleteButton from '../Generic/DeleteButton';
import SortableHeaderCell from '../Generic/SortableHeaderCell';
import { logInfo } from '../../config_and_helpers/logger';
import { resolveBudgetPiId } from '../../utils/budgetListRow';
import BudgetDetailModal from './BudgetDetailModal';
import '../Glass/GlassStyle.css';

const LOG_CONTEXT = 'BudgetsTable';

/**
 * Format PI display from nested primary_investigator.
 *
 * @param {Object|null|undefined} line
 * @returns {string}
 */
export function formatBudgetPiLabel(line) {
  const pi = line?.primary_investigator;
  if (!pi) return '—';
  const name = `${pi.first_name || ''} ${pi.last_name || ''}`.trim();
  return name || pi.username || '—';
}

/**
 * Sortable institutional Budgets table for Procure admin.
 *
 * @param {Object} props
 * @param {Array} props.budgetsList
 * @param {string} [props.sortBy]
 * @param {string} [props.sortDirection]
 * @param {Function} [props.onSort]
 * @param {Function} props.onEdit
 * @param {Function} [props.onDeleted] - Called with budget id after confirmed delete.
 */
const BudgetsTable = ({
  budgetsList = [],
  sortBy = '',
  sortDirection = 'asc',
  onSort = null,
  onEdit,
  onDeleted,
}) => {
  const isViewer = useIsLabViewer();
  return (
  <div className="budgets-table-wrap">
    <Table striped bordered hover responsive className="mb-0">
      <thead>
        <tr className="text-center">
          <th>#</th>
          <SortableHeaderCell
            label="Number"
            sortKey="number"
            sortBy={sortBy}
            sortDirection={sortDirection}
            onSort={onSort}
          />
          <SortableHeaderCell
            label="Name"
            sortKey="name"
            sortBy={sortBy}
            sortDirection={sortDirection}
            onSort={onSort}
          />
          <th>PI</th>
          <SortableHeaderCell
            label="Allocated"
            sortKey="allocated_amount"
            sortBy={sortBy}
            sortDirection={sortDirection}
            onSort={onSort}
          />
          <th>Currency</th>
          <th>Active</th>
          <th>Orders</th>
          {!isViewer ? <th>Actions</th> : null}
        </tr>
      </thead>
      <tbody>
        {budgetsList.map((row, index) => (
          <tr key={row.id} className="text-center align-middle">
            <td>{index + 1}</td>
            <td>
              <BudgetDetailModal
                key={`${row.id}:${resolveBudgetPiId(row) || 'none'}`}
                budgetObj={row}
                budgetId={row.id}
              />
            </td>
            <td>{row.name || '—'}</td>
            <td>{formatBudgetPiLabel(row)}</td>
            <td>{row.allocated_amount ?? '—'}</td>
            <td>{row.currency || '—'}</td>
            <td>{row.is_active === false ? 'No' : 'Yes'}</td>
            <td>
              {typeof row.order_count === 'number' ? row.order_count : '—'}
            </td>
            {!isViewer && (
            <td className="d-flex flex-row align-items-center justify-content-evenly">
              <Button
                variant="outline-warning"
                size="sm"
                aria-label={`Edit ${row.number || row.name || row.id}`}
                onClick={() => {
                  logInfo(LOG_CONTEXT, 'edit clicked', { id: row.id });
                  onEdit?.(row);
                }}
              >
                <PencilFill />
              </Button>
              <DeleteButton
                objectType="budget"
                objectName={row.number || row.name || `Budget #${row.id}`}
                objectId={row.id}
                deleteFetchFunc={deleteBudgetLine}
                onSuccessfulDelete={() => onDeleted?.(row.id)}
              />
            </td>
            )}
          </tr>
        ))}
      </tbody>
    </Table>
  </div>
  );
};

export default BudgetsTable;
